import {
  Heading,
  modularScale,
  palette,
  VerticalSpacing,
  Middle,
  media,
  responsive,
  pxToRem,
} from "@ottatech/design";
import { useEffect } from "react";
import styled from "styled-components";
import { useQuery } from "@apollo/client";
import { Loading } from "@ottatech/shared-components";
import { pushAnalyticsEvent, useExperiment } from "@ottatech/analytics";
import { motion } from "framer-motion";

import { BatchCardData, BatchCards, BatchCounts } from "./BatchCards";

import { PromptNotification } from "pages/Dashboard/Notifications/Prompts";
import {
  ThemedBatchesDocument,
  ThemedBatchesQuery,
  ThemeId,
  TopSectorDocument,
  TopSectorQuery,
} from "schema";
import { toQueryKey } from "constants/themedBatches";

const BatchContainer = styled(motion.div)`
  ${responsive`padding: ${modularScale({ mobile: 0.5, desktop: 1 })};`}
  background-color: ${palette.brand.yellow};
  margin-bottom: ${pxToRem(36)};
  position: relative;
`;

/**
 * Replaces the contents of BatchContainer when loading,
 * hence the position: absolute
 */
const LoadingWrapper = styled.div`
  align-items: center;
  position: absolute;
  inset: 0 0 0 0;
  display: flex;
`;

const Title = styled(Heading)`
  margin-bottom: 1rem;
  ${responsive`
    font-size: ${modularScale({ mobile: 2, desktop: 6 })};
  `}
  grid-area: title;
`;

const HideWhenLoading = styled.div<{ $loading: boolean }>`
  opacity: ${p => (p.$loading ? "0" : "100")};
  transition: opacity 50ms ease-in;
`;

const TitleGrid = styled.div`
  ${media({ from: "tablet" })`
    display: grid;
    padding-top: 2.8rem;
    grid-gap: 2rem;
    grid-template-columns: repeat(4, 1fr);
    grid-template-areas: "title title prompt prompt"
  `}
  ${media({ from: "desktop" })`
    padding-top: 3.8rem;
  `}
`;

const StyledPromptNotification = styled(PromptNotification)`
  grid-area: prompt;
  ${media({ from: "tablet" })`
    justify-self: right;
  `}
`;

function counts(number: number): BatchCounts {
  return Object.values(ThemeId).reduce(
    (prev, curr) => ({ ...prev, [curr]: number }),
    { ALL_MATCHES: number } as BatchCounts
  );
}

function extractCounts(data?: ThemedBatchesQuery): BatchCounts | undefined {
  const user = data?.currentUser;
  return user
    ? Object.values(ThemeId).reduce<BatchCounts>(
        (acc, theme) => ({ ...acc, [theme]: user[toQueryKey(theme)].length }),
        {
          ALL_MATCHES: data.currentUser?.allMatches.length ?? 0,
        } as BatchCounts
      )
    : undefined;
}

function findTopSector(
  result: TopSectorQuery
): { id: string; value: string } | null {
  switch (result.me?.__typename) {
    case "CurrentCandidate":
      return result.me.topSector;
    case "CurrentAdmin":
      return result.me.topSector;
    default:
      return null;
  }
}

export function useBatchCardData(): BatchCardData | "loading" | undefined {
  const { data: topData, loading: topLoading } = useQuery(TopSectorDocument);
  const topSector = topData ? findTopSector(topData) : null;
  const preferredSectorId: string = topSector ? topSector.id : "";
  const { data, loading } = useQuery(ThemedBatchesDocument, {
    variables: { preferredSectorId },
    fetchPolicy: "network-only",
    skip: !topSector,
  });

  const counts = extractCounts(data);

  if (topLoading || loading) {
    return "loading";
  } else if (!topSector || !counts) {
    return undefined;
  } else {
    return { counts: counts, sector: topSector };
  }
}

function analyticsTags(bc: BatchCounts): Record<string, number> {
  const es = Object.entries(bc) as [ThemeId | "ALL_MATCHES", number][];
  return Object.fromEntries(es.map(([k, v]) => [`${toQueryKey(k)}Count`, v]));
}

function useAnalyticsEvent(data?: BatchCardData | "loading") {
  useEffect(() => {
    if (data !== undefined && data !== "loading") {
      pushAnalyticsEvent({
        eventName: "Candidate Viewed Dashboard",
        topSectorName: data.sector.value,
        topSectorId: data.sector.id,
        ...analyticsTags(data.counts),
      });
    }
  }, [data]);
}

/**
 * Intepret the resulting batch card data from the backend
 * If it is loading we return non empty batches so the UI doesn't jump when jobs appear
 * otherwise if there's no data at all we hard code a preferred sector
 */
export function findData(input?: BatchCardData | "loading"): BatchCardData {
  const sector = { id: "38", value: "Sustainability" };
  if (input === "loading") {
    return { counts: counts(1), sector };
  } else if (input === undefined) {
    return { counts: counts(0), sector };
  } else {
    return input;
  }
}

export function ThemedBatches({
  greeting,
}: {
  greeting: string | null;
}): React.ReactElement {
  const data = useBatchCardData();
  useAnalyticsEvent(data);

  const {
    variant: profilePerformanceNotificationVariant,
    loading: profilePerformanceExperimentLoading,
  } = useExperiment("20220822_PROFILE_PERFORMANCE_NOTIFICATION");

  const profilePerformancePromptEnabled =
    profilePerformanceNotificationVariant === "prompt" &&
    !profilePerformanceExperimentLoading;

  return (
    <BatchContainer layout initial={false}>
      <Middle maxWidth={1170} textAlign="inherit">
        {data === "loading" && (
          <LoadingWrapper data-testid="loading">
            <Loading />
          </LoadingWrapper>
        )}
        <VerticalSpacing size={1}>
          <HideWhenLoading $loading={!greeting}>
            <TitleGrid>
              <Title>{greeting || "Welcome"}</Title>
              {profilePerformancePromptEnabled && <StyledPromptNotification />}
            </TitleGrid>
          </HideWhenLoading>
          <HideWhenLoading $loading={data === "loading"}>
            <BatchCards {...findData(data)} />
          </HideWhenLoading>
        </VerticalSpacing>
      </Middle>
    </BatchContainer>
  );
}
